import API from '../../services/api';

// Fetch all users for approval flows
export const fetchUsers = async () => {
  try {
    const response = await API.get('/approval/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// NOTE: 7 legacy approval-flow API functions were removed in Phase 2 — they
// targeted endpoints the backend no longer serves (createOrUpdateApprovalFlow,
// getObjectsForApprovalFlow, assignApproversToStep, startApprovalProcess,
// updateApprovalStepStatus, /approval/pending, getMyInitiatedApprovals) and had
// zero importers. Use initiateApproval / progressApprovalInstance /
// fetchApprovalInstance instead.

// ── Phase 2: Workflow Engine foundation ──

// Fetch registered entity types (drives dynamic "My Approvals" tabs/columns).
export const fetchEntityTypes = async () => {
  const response = await API.get('/approval/entity-types');
  return response.data.data || [];
};

// Fetch canonical permission roles.
export const fetchPermissionRoles = async () => {
  const response = await API.get('/approval/permission-roles');
  return response.data.data || [];
};

// Fetch assignment strategies.
export const fetchAssignmentStrategies = async () => {
  const response = await API.get('/approval/assignment-strategies');
  return response.data.data || [];
};

// Fetch the aggregated fact catalogue (drives the rule builder).
export const fetchFactCatalogue = async () => {
  const response = await API.get('/approval/facts');
  return response.data.data || [];
};

// Fetch bill details by ID
export const fetchBillById = async (billId) => {
  try {
    const response = await API.get(`/bills/${billId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching bill details:', error);
    throw error;
  }
};

// Fetch approval instances with flexible filters and pagination
export const fetchApprovalInstance = async ({
  module,
  entityType,
  objectId,
  instanceId,
  userId,
  status,
  page,
  limit,
  search,
}) => {
  const params = {};
  if (module) params.module = module;
  if (entityType) params.entityType = entityType;
  if (objectId) params.objectId = objectId;
  if (instanceId) params.instanceId = instanceId;
  if (userId) params.userId = userId;
  if (status !== undefined) params.status = status;
  if (page !== undefined) params.page = page;
  if (limit !== undefined) params.limit = limit;
  if (search) params.search = search;

  const response = await API.get('/approval/instances', { params });
  return response.data;
};

// Mark an approval instance as viewed (timeline event).
export const markApprovalViewed = async (instanceId) => {
  const response = await API.post('/approval/viewed', { instanceId });
  return response.data;
};

// Withdraw (cancel) an in-flight approval.
export const withdrawApproval = async ({ instanceId, reason }) => {
  const response = await API.post('/approval/withdraw', { instanceId, reason });
  return response.data;
};

// Reassign the current step's approvers (admin).
export const reassignApprovalStep = async ({ instanceId, approvers }) => {
  const response = await API.post('/approval/reassign', { instanceId, approvers });
  return response.data;
};

// Fetch pending approval instances for a user
export const fetchPendingApprovalInstances = async (userId) => {
  const response = await API.get('/approval/instances', {
    params: { userId, status: 'pending' },
  });
  return response.data;
};

// Progress an approval instance (approve/reject)
export const progressApprovalInstance = async ({ instanceId, action, comment, payload }) => {
  // `payload` carries input that only exists at decision time — e.g. the bank UTRs
  // an approver supplies when approving a payment. Omitted for decisions that
  // need none, which is every entity type except PAYMENT_APPROVAL today.
  // Always send Authorization header
  const response = await API.post(
    '/approval/progress',
    { instanceId, action, comment, ...(payload ? { payload } : {}) },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }
  );
  return response.data;
};

// Fetch all approval templates
export const fetchApprovalTemplates = async () => {
  try {
    const response = await API.get('/approval/template');
    console.log('[API] fetchApprovalTemplates full response:', response);
    console.log('[API] fetchApprovalTemplates response.data:', response.data);
    
    // Backend returns { data: [...], count: n, message: "..." }
    // So we need response.data.data to get the actual templates array
    return response.data.data || response.data || [];
  } catch (error) {
    console.error('[API] Error fetching approval templates:', error);
    throw error;
  }
};

// Initiate a new approval instance
export const initiateApproval = async ({ module, objectId, templateId, context }) => {
  const response = await API.post('/approval/initiate', {
    module,
    objectId,
    templateId,
    context,
  });
  return response.data;
};

// Create a new approval template
export const createApprovalTemplate = async (payload) => {
  const response = await API.post('/approval/template', payload);
  return response.data;
};

// Update an approval template by ID
export const updateApprovalTemplate = async (id, payload) => {
  const response = await API.put(`/approval/template/${id}`, payload);
  return response.data;
};

export const updateTemplateStatus = async (id, status) => {
  const response = await API.patch(`/approval/template/${id}/status`, { status });
  return response.data;
};

// Delete an approval template by ID
export const deleteApprovalTemplate = async (id) => {
  const response = await API.delete(`/approval/template/${id}`);
  return response.data;
};

// What extra input this decision needs before it can be approved. Returns
// { kind: 'NONE' } for entity types that need nothing, or
// { kind: 'PAYMENT_SPLITS', amount, splits, bankModes, referencesRequired, editable }
// for a payment, whose bank UTRs the approver supplies at approval time.
// Read live from the payment record, so requests raised before this existed work too.
export const fetchDecisionForm = async (instanceId) => {
  const response = await API.get('/approval/decision-form', { params: { instanceId } });
  return response.data?.data || null;
};

// The full business record behind an approval — every line of a manual journal
// entry, the entry a delete request targets, and so on. Returns
// { kind: 'NONE' } for entity types whose module registered no previewProvider,
// in which case the detail page shows the displayMeta panel alone.
export const fetchEntityPreview = async (instanceId) => {
  const response = await API.get('/approval/entity-preview', { params: { instanceId } });
  return response.data?.data || null;
};
